import React from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Theme,
  useTheme,
  IconButton,
  Tooltip,
  Chip,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { CheckCircle, Cancel, HourglassEmpty } from "@mui/icons-material";
import AppTableHeaderCell from "../common/pages/AppTableHeaderCell";
import AppTablePagenation from "../common/pages/AppTablePagenation";
import { SortType } from "../../../models/SortType";
import { formatDate } from "../common/functions/formatDate";
import AppTableRowCell from "../common/pages/AppTableRowCell";
import { Vendor } from "../../../models/Vendors/Vendor";
import CustomTypography from "../common/form/CustomTypography";
import { useNotification } from "../../../utils/NotificationsContext";
import { HelperAPI } from "../../../api/HelperAPI";
import { ensureUrlProtocol } from "../../../utils/urlHelper";

interface VendorRequestTableProp {
  maxHeight: number;
  vendors: Vendor[];
  limit: number;
  page_no: number;
  total_pages: number;
  total_count: number;
  sortBy?: string;
  sortType?: SortType;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
  onSortChange: (column: string, sortType: SortType) => void;
  onFinanceApprove?: (vendorId: number) => void;
  onFinanceReject?: (vendorId: number) => void;
  onSourcingApprove?: (vendorId: number) => void;
  onSourcingReject?: (vendorId: number) => void;
  onViewDetails?: (vendorId: number) => void;
}

const VendorRequestTable = ({
  maxHeight,
  vendors,
  page_no,
  limit,
  total_count,
  sortBy,
  sortType,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
  onFinanceApprove,
  onFinanceReject,
  onSourcingApprove,
  onSourcingReject,
  onViewDetails,
}: VendorRequestTableProp) => {
  const theme = useTheme<Theme>();
  const { showError } = useNotification();

  const handleRowsChange = (value: number) => {
    onPageChange(null, 1);
    onRowsPerPageChange(value);
  };

  const renderSortIcon = (property: string) => {
    if (sortBy === property) {
      return sortType === SortType.ASC ? "↑" : "↓";
    }
    return "↑↓";
  };

  const handleDownloadLink = (url: string, defaultFilename: string | null) => {
    if (!url || url.trim() === "") return;
    HelperAPI.downloadVendorFiles(url, defaultFilename).catch((err) => {
      showError(
        "Download failed",
        err?.response?.data?.message || "Failed to download file",
      );
    });
  };

  const getApprovalStatus = (vendor: Vendor) => {
    const financeApproved = vendor.finance_approval === 1;
    const financeRejected = vendor.finance_rejection === 1;
    const sourcingApproved = vendor.sourcing_approval === 1;
    const sourcingRejected = vendor.sourcing_rejection === 1;

    if (financeRejected || sourcingRejected) {
      return {
        status: "rejected",
        icon: <Cancel sx={{ color: theme.palette.error.main }} />,
        tooltip: "Rejected",
      };
    }

    if (financeApproved && sourcingApproved) {
      return {
        status: "approved",
        icon: <CheckCircle sx={{ color: theme.palette.success.main }} />,
        tooltip: "Approved",
      };
    }

    return {
      status: "pending",
      icon: <HourglassEmpty sx={{ color: theme.palette.warning.main }} />,
      tooltip: "Pending",
    };
  };

  return (
    <Paper
      sx={{
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        overflow: "hidden",
      }}
    >
      {vendors.length > 0 ? (
        <Box>
          <TableContainer sx={{ maxHeight: maxHeight }}>
            <Table stickyHeader>
              <TableHead sx={{ position: "relative", zIndex: 30 }}>
                <TableRow>
                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 14,
                      position: "sticky",
                      left: 0,
                      top: 0,
                      zIndex: 30,
                      minWidth: "60px",
                      backgroundColor: theme.palette.background.paper,
                      isolation: "isolate",
                    }}
                  >
                    <TableSortLabel
                      active={sortBy === "id"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange(
                          "id",
                          sortBy === "id" && sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC,
                        )
                      }
                      IconComponent={() => <span>{renderSortIcon("id")}</span>}
                      sx={{ fontSize: "12px" }}
                    >
                      ID
                    </TableSortLabel>
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 14,
                      minWidth: "120px",
                    }}
                  >
                    <TableSortLabel
                      active={sortBy === "name"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange(
                          "name",
                          sortBy === "name" && sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC,
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("name")}</span>
                      )}
                      sx={{ fontSize: "12px" }}
                    >
                      Name
                    </TableSortLabel>
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 14,
                      width: "200px",
                      minWidth: "200px",
                      maxWidth: "200px",
                    }}
                  >
                    Email
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 14,
                      minWidth: "120px",
                    }}
                  >
                    Rejected
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 14,
                      minWidth: "100px",
                    }}
                  >
                    Approval
                  </AppTableHeaderCell>

                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 14,
                      minWidth: "120px",
                    }}
                  >
                    GST Number
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 14,
                      minWidth: "120px",
                    }}
                  >
                    PAN Number
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 14,
                      minWidth: "100px",
                    }}
                  >
                    Payment Term
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 14,
                      minWidth: "100px",
                    }}
                  >
                    PAN Card
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 14,
                      minWidth: "100px",
                    }}
                  >
                    GST Certificate
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 14,
                      minWidth: "100px",
                    }}
                  >
                    Bank Details
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 14,
                      minWidth: "100px",
                    }}
                  >
                    MSME
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 14,
                      minWidth: "100px",
                    }}
                  >
                    MSME Declaration
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 14,
                      minWidth: "120px",
                    }}
                  >
                    Company Website
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 14,
                      minWidth: "150px",
                    }}
                  >
                    Products Catalog
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 14,
                      minWidth: "150px",
                    }}
                  >
                    Types
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 14,
                      minWidth: "150px",
                    }}
                  >
                    Supply Type
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 14,
                      minWidth: "150px",
                    }}
                  >
                    Creator
                  </AppTableHeaderCell>
                  <AppTableHeaderCell fontSize="0.8rem">
                    <TableSortLabel
                      active={sortBy === "created_date"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange(
                          "created_date",
                          sortBy === "created_date" &&
                            sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC,
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("created_date")}</span>
                      )}
                      sx={{ fontSize: "0.8rem" }}
                    >
                      Created Date
                    </TableSortLabel>
                  </AppTableHeaderCell>
                  <AppTableHeaderCell fontSize="0.8rem">
                    <TableSortLabel
                      active={sortBy === "modified_date"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange(
                          "modified_date",
                          sortBy === "modified_date" &&
                            sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC,
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("modified_date")}</span>
                      )}
                      sx={{ fontSize: "0.8rem" }}
                    >
                      Modified Date
                    </TableSortLabel>
                  </AppTableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vendors.map((vendor) => {
                  return (
                    <TableRow
                      key={vendor.id}
                      hover
                      sx={{
                        "&:nth-of-type(odd)": {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    >
                      <AppTableRowCell
                        sx={{
                          fontSize: "12px",
                          textAlign: "center",
                          left: 0,
                          position: "sticky",
                          zIndex: 10,
                          boxShadow: "10px 0 20px -12px rgba(115,115,115,0.3)",
                          backgroundColor: theme.palette.background.paper,
                          fontWeight: 600,
                          minWidth: "80px",
                          padding: "8px 8px",
                        }}
                      >
                        {onViewDetails ? (
                          <Box
                            onClick={() => onViewDetails(vendor.id!)}
                            sx={{
                              color: theme.palette.primary.main,
                              textDecoration: "underline",
                              cursor: "pointer",
                              "&:hover": {
                                textDecoration: "underline",
                                color: theme.palette.primary.dark,
                              },
                            }}
                          >
                            {vendor.id}
                          </Box>
                        ) : (
                          vendor.id
                        )}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{
                          fontSize: "12px",
                          textAlign: "center",
                          padding: "8px 8px",
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minWidth: "200px",
                          maxWidth: "200px",
                        }}
                      >
                        {vendor.name || "-"}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{
                          fontSize: "12px",
                          textAlign: "center",
                          width: "200px",
                          minWidth: "200px",
                          maxWidth: "200px",
                          padding: "8px 4px",
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                        }}
                      >
                        {vendor.email || "-"}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{
                          fontSize: "12px",
                          textAlign: "center",
                          padding: "8px 4px",
                        }}
                      >
                        {vendor.rejection_history &&
                          vendor.rejection_history.length > 0
                          ? vendor.rejection_history.length + " times"
                          : "-"}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{
                          fontSize: "12px",
                          textAlign: "center",
                          padding: "8px 4px",
                        }}
                      >
                        <Tooltip title={getApprovalStatus(vendor).tooltip}>
                          {getApprovalStatus(vendor).icon}
                        </Tooltip>
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{
                          fontSize: "12px",
                          textAlign: "center",
                          padding: "8px 4px",
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                        }}
                      >
                        {vendor.gst_number || "-"}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{
                          fontSize: "12px",
                          textAlign: "center",
                          padding: "8px 4px",
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                        }}
                      >
                        {vendor.pan_number || "-"}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{
                          fontSize: "12px",
                          textAlign: "center",
                          padding: "8px 4px",
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                        }}
                      >
                        {vendor.payment_term + " Days" || "-"}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{
                          fontSize: "12px",
                          textAlign: "center",
                          padding: "8px 4px",
                        }}
                      >
                        {vendor.pan_card_link ? (
                          <Tooltip title="Download PAN Card">
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleDownloadLink(
                                  vendor.pan_card_link!,
                                  `PAN_Card_${vendor.id}.pdf`,
                                )
                              }
                              sx={{
                                color: theme.palette.primary.main,
                                "&:hover": {
                                  backgroundColor: theme.palette.action.hover,
                                },
                              }}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          "-"
                        )}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{
                          fontSize: "12px",
                          textAlign: "center",
                          padding: "8px 4px",
                        }}
                      >
                        {vendor.gstin_certificate_link ? (
                          <Tooltip title="Download GST Certificate">
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleDownloadLink(
                                  vendor.gstin_certificate_link!,
                                  `GST_Certificate_${vendor.id}.pdf`,
                                )
                              }
                              sx={{
                                color: theme.palette.primary.main,
                                "&:hover": {
                                  backgroundColor: theme.palette.action.hover,
                                },
                              }}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          "-"
                        )}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{
                          fontSize: "12px",
                          textAlign: "center",
                          padding: "8px 4px",
                        }}
                      >
                        {vendor.bank_details_link ? (
                          <Tooltip title="Download Bank Details">
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleDownloadLink(
                                  vendor.bank_details_link!,
                                  `Bank_Details_${vendor.id}.pdf`,
                                )
                              }
                              sx={{
                                color: theme.palette.primary.main,
                                "&:hover": {
                                  backgroundColor: theme.palette.action.hover,
                                },
                              }}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          "-"
                        )}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{
                          fontSize: "12px",
                          textAlign: "center",
                          padding: "8px 4px",
                        }}
                      >
                        {vendor.msme_link && vendor.msme_link.trim() !== "" ? (
                          <Tooltip title="Download MSME Certificate">
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleDownloadLink(
                                  vendor.msme_link!,
                                  `MSME_${vendor.id}.pdf`,
                                )
                              }
                              sx={{
                                color: theme.palette.primary.main,
                                "&:hover": {
                                  backgroundColor: theme.palette.action.hover,
                                },
                              }}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          "-"
                        )}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{
                          fontSize: "12px",
                          textAlign: "center",
                          padding: "8px 4px",
                        }}
                      >
                        {vendor.msme_declaration_link &&
                          vendor.msme_declaration_link.trim() !== "" ? (
                          <Tooltip title="Download MSME Declaration">
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleDownloadLink(
                                  vendor.msme_declaration_link!,
                                  `MSME_Declaration_${vendor.id}.pdf`,
                                )
                              }
                              sx={{
                                color: theme.palette.primary.main,
                                "&:hover": {
                                  backgroundColor: theme.palette.action.hover,
                                },
                              }}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          "-"
                        )}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{
                          fontSize: "12px",
                          textAlign: "center",
                          padding: "8px 4px",
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                        }}
                      >
                        {vendor.company_website_link &&
                          vendor.company_website_link.trim() !== "" ? (
                          <Box
                            component="a"
                            href={ensureUrlProtocol(
                              vendor.company_website_link,
                            )}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: theme.palette.primary.main,
                              textDecoration: "underline",
                              cursor: "pointer",
                              "&:hover": {
                                textDecoration: "underline",
                              },
                            }}
                          >
                            Visit
                          </Box>
                        ) : (
                          "-"
                        )}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{
                          fontSize: "12px",
                          textAlign: "center",
                          padding: "8px 4px",
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                        }}
                      >
                        {vendor.products_catalog_link &&
                          vendor.products_catalog_link.trim() !== "" ? (
                          <Box
                            component="a"
                            href={ensureUrlProtocol(
                              vendor.products_catalog_link,
                            )}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: theme.palette.primary.main,
                              textDecoration: "underline",
                              cursor: "pointer",
                              "&:hover": {
                                textDecoration: "underline",
                              },
                            }}
                          >
                            Visit
                          </Box>
                        ) : (
                          "-"
                        )}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{
                          fontSize: "12px",
                          textAlign: "center",
                          padding: "8px 4px",
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                        }}
                      >
                        {vendor.types && vendor.types.length > 0 ? (
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 0.5,
                              justifyContent: "center",
                            }}
                          >
                            {vendor.types.map((type, index) => (
                              <Box
                                key={type.type_id || index}
                                sx={{
                                  px: 1,
                                  py: 0.25,
                                  backgroundColor:
                                    theme.palette.primary.main + "15",
                                  borderRadius: 1,
                                  fontSize: "0.7rem",
                                  color: theme.palette.primary.main,
                                  fontWeight: 500,
                                }}
                              >
                                {type.type}
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          "-"
                        )}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{
                          fontSize: "12px",
                          textAlign: "center",
                          padding: "8px 4px",
                        }}
                      >
                        {vendor.supplier_type || "-"}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{
                          fontSize: "12px",
                          textAlign: "center",
                          padding: "8px 4px",
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                        }}
                      >
                        {vendor.creator_name || "-"}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{
                          fontSize: "12px",
                          textAlign: "center",
                          padding: "8px 4px",
                        }}
                      >
                        {formatDate(vendor.created_date)}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{
                          fontSize: "12px",
                          textAlign: "center",
                          padding: "8px 4px",
                        }}
                      >
                        {formatDate(vendor.modified_date)}
                      </AppTableRowCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <AppTablePagenation
            fontSize="12px"
            pageNo={page_no}
            limit={limit}
            totalCount={total_count}
            onPageChange={onPageChange}
            handleRowsChange={handleRowsChange}
          />
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "200px",
            color: theme.palette.text.secondary,
          }}
        >
          <CustomTypography variant="body2">No data available</CustomTypography>
        </Box>
      )}
    </Paper>
  );
};

export default VendorRequestTable;
